/* eslint-disable @next/next/no-img-element */
import type { FC, ReactNode, KeyboardEvent, ComponentType } from 'react'
import type { LucideProps } from 'lucide-react'
import type { NavLink } from '@/constants/navigation'

import { useEffect, useRef, useState } from 'react'
import Link from 'next/link'
import { useRouter } from 'next/router'

import {
    BookOpen,
    Building2,
    Home,
    LifeBuoy,
    Mail,
    Sparkles,
    Tag
} from 'lucide-react'

import {
    gsap,
    ms,
    registerGsap,
    useGSAP,
    wfEase
} from '@/lib/gsap'
import { routes } from '@/lib/routes'

import {
    megaMenuItems,
    primaryLinks,
    secondaryLinks,
    socialLinks
} from '@/constants/navigation'

const megaIcons: Record<string, ComponentType<LucideProps>> = {
    home: Home,
    sparkles: Sparkles,
    tag: Tag,
    building: Building2,
    lifebuoy: LifeBuoy,
    mail: Mail,
    book: BookOpen
}

const NavItem: FC<{ link: NavLink; active: boolean; onNavigate: () => void }> = ({ link, active, onNavigate }): ReactNode => (
    <div className='nav-link-holder'>
        <Link
            href={link.href}
            onClick={onNavigate}
            aria-current={active ? 'page' : undefined}
            className={`nav-link w-inline-block${active ? ' w--current' : ''}`}
        >
            <div>{link.label}</div>
            <div className='link-background' />
        </Link>
    </div>
)

const MegaItem: FC<{
    label: string
    href: string
    description: string
    icon: string
    onNavigate: () => void
}> = ({ label, href, description, icon, onNavigate }): ReactNode => {
    const Icon = megaIcons[icon] ?? Home

    return (
        <Link href={href} onClick={onNavigate} className='mega-item'>
            <span className='mega-item-icon'>
                <Icon className='size-5' strokeWidth={2} />
            </span>
            <span className='mega-item-text'>
                <span className='mega-item-title'>{label}</span>
                <span className='mega-item-desc'>{description}</span>
            </span>
        </Link>
    )
}

export const Nav: FC = (): ReactNode => {
    const router = useRouter()
    const navRef = useRef<HTMLElement | null>(null)
    const dropdownRef = useRef<HTMLDivElement | null>(null)

    const [mobileOpen, setMobileOpen] = useState(false)
    const [dropdownOpen, setDropdownOpen] = useState(false)
    const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

    const currentPath = router.asPath.split('?')[0]
    const isActive = (href: string): boolean => (href === '/' ? currentPath === '/' : currentPath.startsWith(href))

    const clearCloseTimer = (): void => {
        if (closeTimer.current) {
            clearTimeout(closeTimer.current)
            closeTimer.current = null
        }
    }

    // Open immediately on hover; close with a short grace period so moving the
    // cursor from the "Pages" toggle into the panel doesn't dismiss the menu.
    const openDropdown = (): void => {
        clearCloseTimer()
        setDropdownOpen(true)
    }

    const scheduleCloseDropdown = (): void => {
        clearCloseTimer()
        closeTimer.current = setTimeout(() => setDropdownOpen(false), 180)
    }

    const closeMenus = (): void => {
        clearCloseTimer()
        setMobileOpen(false)
        setDropdownOpen(false)
    }

    useEffect(() => () => clearCloseTimer(), [])

    useGSAP(
        () => {
            registerGsap()
            if (!dropdownRef.current) return

            gsap.to(dropdownRef.current, {
                autoAlpha: dropdownOpen ? 1 : 0,
                y: dropdownOpen ? 0 : -10,
                duration: ms(300),
                ease: wfEase('ease')
            })
        },
        { dependencies: [dropdownOpen], scope: navRef }
    )

    useGSAP(
        () => {
            registerGsap()
            if (!mobileOpen) return

            const menu = navRef.current?.querySelector('.nav-menu')
            if (menu) {
                gsap.fromTo(
                    menu,
                    { opacity: 0, y: -10 },
                    { opacity: 1, y: 0, duration: ms(300), ease: wfEase('ease') }
                )
            }
        },
        { dependencies: [mobileOpen], scope: navRef }
    )

    const onToggleKey = (event: KeyboardEvent<HTMLDivElement>): void => {
        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault()
            setDropdownOpen(open => !open)
        }
    }

    return (
        <header
            ref={navRef}
            role='banner'
            data-collapse='medium'
            data-animation='default'
            className='main-nav w-nav'
            data-nav-open={mobileOpen ? 'true' : 'false'}
        >
            <div className='container nav-container w-container'>
                <div className='nav-bg-holder'>
                    <div className='nav-bg' />
                </div>

                <Link href={routes.home} onClick={closeMenus} className='brand w-nav-brand'>
                    <img src='/images/SanDiego-Logo.svg' alt='San Diego' className='nav-logo' />
                </Link>

                <nav role='navigation' className='nav-menu w-nav-menu'>
                    {primaryLinks.map(link => (
                        <NavItem key={link.label} link={link} active={isActive(link.href)} onNavigate={closeMenus} />
                    ))}

                    <div className='nav-link-holder'>
                        <div
                            className='nav-dropdown-parent w-dropdown'
                            onMouseEnter={openDropdown}
                            onMouseLeave={scheduleCloseDropdown}
                        >
                            <div
                                role='button'
                                tabIndex={0}
                                aria-haspopup='true'
                                aria-expanded={dropdownOpen}
                                onClick={() => setDropdownOpen(open => !open)}
                                onKeyDown={onToggleKey}
                                className='nav-link nav-link-dropdown-btn w-dropdown-toggle'
                            >
                                <div className='nav-link-icon w-icon-dropdown-toggle' />
                                <div>Pages</div>
                                <div className='link-background' />
                                <div className='nav-dropdown-link-arrow-holder'>
                                    <div className='nav-dropdown-link-arrow-animated'>
                                        <div className='nav-dropdown-link-arrow' />
                                    </div>
                                </div>
                            </div>

                            <nav
                                ref={dropdownRef}
                                className={`nav-dropdown-content-holder w-dropdown-list${dropdownOpen ? ' w--open' : ''}`}
                            >
                                <div className='mega-menu'>
                                    <div className='mega-menu-grid'>
                                        {megaMenuItems.map(item => (
                                            <MegaItem
                                                key={item.label}
                                                label={item.label}
                                                href={item.href}
                                                description={item.description}
                                                icon={item.icon}
                                                onNavigate={closeMenus}
                                            />
                                        ))}
                                    </div>

                                    <div className='mega-menu-footer'>
                                        <div className='mega-footer-links'>
                                            {secondaryLinks.map(link => (
                                                <Link
                                                    key={link.label}
                                                    href={link.href}
                                                    onClick={closeMenus}
                                                    className='mega-footer-link'
                                                >
                                                    {link.label}
                                                </Link>
                                            ))}
                                        </div>

                                        <div className='mega-footer-social'>
                                            {socialLinks.map(social => (
                                                <a
                                                    key={social.label}
                                                    href={social.href}
                                                    target='_blank'
                                                    rel='noreferrer'
                                                    aria-label={social.label}
                                                    className='mega-social-link'
                                                >
                                                    <img src={social.icon} alt='' aria-hidden='true' />
                                                </a>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            </nav>
                        </div>
                    </div>

                    <div className='nav-cta-button'>
                        <Link href={routes.pricing} onClick={closeMenus} className='button nav-button w-button'>
                            Sales
                        </Link>
                    </div>
                </nav>

                <div
                    role='button'
                    tabIndex={0}
                    aria-label='Menu'
                    aria-expanded={mobileOpen}
                    onClick={() => setMobileOpen(open => !open)}
                    onKeyDown={event => {
                        if (event.key === 'Enter' || event.key === ' ') {
                            event.preventDefault()
                            setMobileOpen(open => !open)
                        }
                    }}
                    className={`menu-button w-nav-button${mobileOpen ? ' w--open' : ''}`}
                >
                    <img src='/images/menu-icon-white.svg' alt='' aria-hidden='true' />
                </div>
            </div>
        </header>
    )
}



